import React, { useCallback, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import "../../../../sass/variables.module.scss";
import Card from "../../common/card/card";
import DetailRow from "../detailsRow";
import "./overview.scss";
import { AddressState } from "slices/address-manager/AddressState";
import { useSelector } from "react-redux";
import { addressSelector } from "slices/address-manager/addressSlice";
import { cn } from "@bem-react/classname";
import Loading from "ui-components/loading/loading";
import { Popover } from "ui-components/popover/popover";
import { useAssociatedAccounts } from "features/address-manager/hooks/useAssociatedAccounts";
import searchIcon from "images/search_gray.svg";
import ServiceDays from "features/address-manager/common/serviceDays/serviceDays";
import LabeledTextInput from "ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import InfiniteScrollableList from "features/address-manager/common/infiniteScrollableList";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "app/data/common/queryKeys";
import AddressmanagerService from "app/data/address-manager/addressService";
import { useVerified } from "features/address-manager/hooks/useVerified";
import { ReactComponent as Clock } from "images/clock.svg";
import { ReactComponent as Reload } from "images/reload.svg";
import { ReactComponent as Calendar } from "images/calendar_outline.svg";
import moment from "moment";
import XGSLeafletMap from "ui-components/map/leaflet/XGSLeafletMap";
import { LeafletMarkerModel } from "ui-components/map/leaflet/xgsLeafletMap.types";
import { ReactComponent as AddressMarker } from "../../../../images/marker.svg";

interface LocationItem {
  label: string;
  value: string | string[];
  verifiedBy?: string;
  verifiedDate?: string;
  status?: string;
}

interface DailyHoursMap {
  dailyHoursMap: {
    [day: string]: {
      fromTime: string;
      toTime: string;
      isOpen: boolean;
    }
  }
}

interface GetReceivingHoursInfo {
  receivingHours: string;
  pickupFrequency: string[];
}

interface AddressDetails {
  locationDetails: {
    items: LocationItem[];
  };
  drivingInstructions: string;
}

const OutlineRow = ({ label, value, variant }: { label: string, value: string | React.ReactNode, variant?: "blue" }) => {
  const cnCard = cn("outline-row");
  return (
    <div className={cnCard()}>
      <div className={cnCard("label")}>{label}: </div>
      <div className={cnCard("value", { variant: variant })}>
        {value}
      </div>
    </div>
  )
}

const OverviewWidget: React.FC = () => {
  const addressManagerState: AddressState = useSelector(addressSelector);
  const address = addressManagerState?.selectedAddress;
  const selectedAddressId = addressManagerState?.selectedAddressId;
  const isLoadingAddressById = addressManagerState?.byId?.isLoading;
  const [search, setSearch] = useState<string>("");
  const { data: verifiedDetails } = useVerified(selectedAddressId || "");
  const { data: associatedAccounts, isLoading: isLoadingAssociatedAccounts, hasNextPage, fetchNextPage } = useAssociatedAccounts(selectedAddressId || "", search);
  const [filter, setFilter] = useState<string>("");

  const { data: nearbyDetails, isLoading: isLoadingNearbyDetails } = useQuery({
    queryKey: [QUERY_KEYS.NEARBY_DETAILS, selectedAddressId],
    queryFn: async () => {
      if (!selectedAddressId) return;
      return await AddressmanagerService.getInstance().getNearbyDetails(selectedAddressId);
    },
    enabled: !!selectedAddressId,
    refetchOnWindowFocus: false,
  })

  const formatTime = useCallback((timeStr: string | null) => {
    if (!timeStr) return "";
    const [h, m] = timeStr.split(":").map(Number);
    const hour = ((h + 11) % 12) + 1;
    const suffix = h >= 12 ? "PM" : "AM";
    return `${hour}:${m.toString().padStart(2, "0")} ${suffix}`;
  }, []);

  const getReceivingHoursInfo = useCallback((data: DailyHoursMap) => {
    const dayOrder = [
      "SUNDAY",
      "MONDAY",
      "TUESDAY",
      "WEDNESDAY",
      "THURSDAY",
      "FRIDAY",
      "SATURDAY",
    ];

    const shortNames: Record<string, string> = {
      SUNDAY: "Sunday",
      MONDAY: "Monday",
      TUESDAY: "Tuesday",
      WEDNESDAY: "Wednesday",
      THURSDAY: "Thursday",
      FRIDAY: "Friday",
      SATURDAY: "Saturday",
    };

    const openDays = dayOrder.filter(day => data.dailyHoursMap[day]?.isOpen);
    if (openDays.length === 0) return { receivingHours: "N/A", pickupFrequency: [] };

    // assume all open days have same hours
    const sampleDay = data.dailyHoursMap[openDays[0]];
    const from = formatTime(sampleDay.fromTime);
    const to = formatTime(sampleDay.toTime);

    // find consecutive sequence(s)
    const indices = openDays.map(day => dayOrder.indexOf(day)).sort((a, b) => a - b);
    const consecutiveGroups: string[][] = [];
    let currentGroup: string[] = [openDays[0]];

    for (let i = 1; i < indices.length; i++) {
      const prevIdx = indices[i - 1];
      const currIdx = indices[i];
      const currDay = dayOrder[currIdx];

      if (currIdx === prevIdx + 1) {
        currentGroup.push(currDay);
      } else {
        consecutiveGroups.push(currentGroup);
        currentGroup = [currDay];
      }
    }
    consecutiveGroups.push(currentGroup);

    // Flatten to single array of readable day names
    const frequencyArray = consecutiveGroups.flat().map(day => shortNames[day]);

    return {
      receivingHours: `${from} – ${to}`,
      pickupFrequency: frequencyArray,
    };
  }, [formatTime]);

  const { data: upcomingDetails } = useQuery({
    queryKey: [QUERY_KEYS.UPCOMING_DETAILS, selectedAddressId],
    queryFn: async () => {
      if (!selectedAddressId) return;
      return await AddressmanagerService.getInstance().getUpcomingDetails(selectedAddressId);
    },
    enabled: !!selectedAddressId,
    refetchOnWindowFocus: false,
  })

  const data: AddressDetails = {
    locationDetails: {
      items: [
        {
          label: "Location Type",
          value: address?.locationType || "N/A",
          verifiedBy: verifiedDetails?.fields?.locationType?.verifiedByName || "",
          verifiedDate: verifiedDetails?.fields?.locationType?.verifiedAt?.toUiMonthDateFormat() || "",
          status: verifiedDetails?.fields?.locationType?.status || ""
        },
        ...(address?.line2 ? [{
          label: "Units / Suites",
          value: address.line2,
          verifiedBy: verifiedDetails?.fields?.line2?.verifiedByName || "",
          verifiedDate: verifiedDetails?.fields?.line2?.verifiedAt?.toUiMonthDateFormat() || "",
          status: verifiedDetails?.fields?.line2?.status || ""
        }] : []),
        {
          label: "Area Type",
          value: address?.areaType ? `Located in ${address?.areaType} Area` : "N/A",
          verifiedBy: verifiedDetails?.fields?.areaType?.verifiedByName || "",
          verifiedDate: verifiedDetails?.fields?.areaType?.verifiedAt?.toUiMonthDateFormat() || "",
          status: verifiedDetails?.fields?.areaType?.status || ""
        },
        {
          label: "Facility Type", value: address?.facilityType || "N/A",
          verifiedBy: verifiedDetails?.fields?.facilityType?.verifiedByName || "",
          verifiedDate: verifiedDetails?.fields?.facilityType?.verifiedAt?.toUiMonthDateFormat() || "",
          status: verifiedDetails?.fields?.facilityType?.status || ""
        },
        {
          label: "Has a Dock",
          value: _.isBoolean(address?.hasDock) ? (address?.hasDock ? "Yes" : "No") : "N/A",
          verifiedBy: verifiedDetails?.fields?.hasDock?.verifiedByName || "",
          verifiedDate: verifiedDetails?.fields?.hasDock?.verifiedAt?.toUiMonthDateFormat() || "",
          status: verifiedDetails?.fields?.hasDock?.status || ""
        },
        {
          label: "Gate Code",
          value: address?.gateCode || "N/A",
          verifiedBy: verifiedDetails?.fields?.gateCode?.verifiedByName || "",
          verifiedDate: verifiedDetails?.fields?.gateCode?.verifiedAt?.toUiMonthDateFormat() || "",
          status: verifiedDetails?.fields?.gateCode?.status || ""
        },
        {
          label: "Gate Code Required",
          value: _.isBoolean(address?.gateCodeRequired) ? (address?.gateCodeRequired ? "Yes" : "No") : "N/A",
          verifiedBy: verifiedDetails?.fields?.gateCodeRequired?.verifiedByName || "",
          verifiedDate: verifiedDetails?.fields?.gateCodeRequired?.verifiedAt?.toUiMonthDateFormat() || "",
          status: verifiedDetails?.fields?.gateCodeRequired?.status || ""
        },
        {
          label: "Limited Access",
          value: _.isBoolean(address?.limitedAccess) ? (address?.limitedAccess ? "Yes" : "No") : "N/A",
          verifiedBy: verifiedDetails?.fields?.limitedAccess?.verifiedByName || "",
          verifiedDate: verifiedDetails?.fields?.limitedAccess?.verifiedAt?.toUiMonthDateFormat() || "",
          status: verifiedDetails?.fields?.limitedAccess?.status || ""
        },
      ],
    },
    drivingInstructions: address?.drivingInstructions || "N/A"
  };

  const items = useMemo(() => associatedAccounts?.pages.flatMap((page) => page.content) ?? [], [associatedAccounts]);
  const itemCount = hasNextPage ? items.length + 1 : items.length;

  const isItemLoaded = useMemo(() => (index: number) => index < items.length, [items]);

  const loadMoreItems = async (startIndex: number, stopIndex: number) => {
    if (hasNextPage) {
      fetchNextPage();
    }
  };

  const Row = React.useCallback(({ index, style }) => {
    if (!isItemLoaded(index)) {
      return <div style={{ ...style, padding: 10 }}>Loading...</div>;
    }

    const result: GetReceivingHoursInfo = getReceivingHoursInfo(associatedAccounts?.pages?.flatMap((page) =>
      page.content)?.[index]?.receivingHours as DailyHoursMap);

    const item = items[index];

    return (
      <div style={style}>
        <ul className="account-item">
          <li>
            <div className="account-item__name" title={item?.name}>{item?.name}</div>
            <div className="account-item__receiving-hours">
              <div className="account-item__receiving-hours__container">
                <Clock />
                <span className="account-item__receiving-hours__label">Receiving Hours:</span>
              </div>
              <span className="account-item__receiving-hours__value">{result?.receivingHours}</span>
            </div>
            <div className="account-item__pickup-frequency">
              <div className="account-item__pickup-frequency__container">
                <Reload />
                <span className="account-item__pickup-frequency__label">Service Days:</span>
              </div>
              <ServiceDays openDaysRange={result?.pickupFrequency || []} />
            </div>
            <div className="account-item__updated">
              Last updated: {item?.dateUpdated?.toUiMonthDateFormat()} <span className="account-item__updated__by">{item?.userUpdated ? "by " + item?.userUpdated : "N/A"}</span></div>
          </li>
        </ul>
      </div>
    );
  }, [items, isItemLoaded, associatedAccounts, getReceivingHoursInfo]);

  useEffect(() => {
    const handler = setTimeout(() => setSearch(filter), 1000);
    return () => clearTimeout(handler);
  }, [filter]);

  useEffect(() => {
    if (
      !window.google ||
      !window.google.maps ||
      !window.google.maps.geometry ||
      !window.google.maps.geometry.encoding ||
      !address ||
      !nearbyDetails?.servicingTerminal?.address
    ) return;

    const encoded = window.google.maps.geometry.encoding.encodePath([
      { lat: Number(address.latitude), lng: Number(address.longitude) },
      {
        lat: Number(nearbyDetails.servicingTerminal.address.latitude),
        lng: Number(nearbyDetails.servicingTerminal.address.longitude),
      },
    ]);

    setPolylineData(encoded);
  }, [address, nearbyDetails]);


  const markers: LeafletMarkerModel[] = [address, nearbyDetails?.servicingTerminal?.address]
    .filter((location): location is NonNullable<typeof location> =>
      location != null &&
      location.latitude != null &&
      location.longitude != null
    )
    .map((location, index) => ({
      id: location?.id?.toString() ?? "",
      kind: "others",
      lat: Number(location.latitude),
      lng: Number(location.longitude),
      customSvg: index === 0 ? {
        component: AddressMarker,
        props: { color: "#008000", width: 36, height: 48 }
      } : {
        component: AddressMarker,
        props: { color: "#EA352B", width: 36, height: 48 }
      },
    }));

  const polyline = {
    id: "polyline",
    positions:  [address, nearbyDetails?.servicingTerminal?.address]
      .filter(t => t?.latitude && t?.longitude)
      .map((t): [number, number] => [
        Number(t?.latitude ?? 0),
        Number(t?.longitude ?? 0)
      ]),
    color: "#000000",
    weight: 4,
    opacity: 0.8,
    tooltip: nearbyDetails?.servicingTerminal?.distanceMiles
      ? `Distance: ${nearbyDetails?.servicingTerminal?.distanceMiles} miles`
      : "N/A",
  };

  return (
    <div className="overview-widget">
      <div className="left-column">
        <Card title="Location Details">
          {isLoadingAddressById ? <div className="loading"><Loading isLoading={true} /></div>
            : data.locationDetails.items.map((item, i) => (
              <DetailRow key={i} {...item} />
            ))}
        </Card>
        <Card title="Proximity Insights">
          {isLoadingNearbyDetails ? <div className="loading"><Loading isLoading={true} /></div> : (
            <>
              <OutlineRow label="Nearest Terminal" value={nearbyDetails?.nearestTerminal ? (nearbyDetails?.nearestTerminal?.airportCode.toString() + " - "
                + nearbyDetails?.nearestTerminal?.terminalName.toString() + " - "
                + nearbyDetails?.nearestTerminal?.terminalNumber.toString()) : "N/A"} variant="blue" />
              <OutlineRow label="Terminal Code" value={nearbyDetails?.nearestTerminal?.terminalNumber.toString() || "N/A"} />
              <OutlineRow label="Distance to Terminal" value={nearbyDetails?.nearestTerminal ? (nearbyDetails?.nearestTerminal?.distanceMiles.toString() + " miles") : "N/A"} />
              <OutlineRow label="Service Days" value={<ServiceDays openDaysRange={nearbyDetails?.nearestTerminal?.terminalOpenHoursList?.[0]?.openDays || ""} />} />
              <h4>Nearby Addresses:</h4>
              <>{(nearbyDetails?.nearestAddresses && nearbyDetails?.nearestAddresses?.length > 0) ? nearbyDetails?.nearestAddresses?.map((addr, i) => (
                <div key={i} className="address-item">
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexDirection: "column",
                    border: "1px solid #4F5F7252",
                    padding: "10px",
                    borderRadius: "4px"
                  }}>
                    <div className="address-line">{addr.line1}</div>
                    <div className="distance">(Within {(addr.distanceMiles * 1609.344).toFixed(0)} meters)</div>
                  </div>
                </div>
              )) : <div className="xgs-sideaddress-list__no-results">No nearby addresses found</div>}
              </>
            </>
          )}
        </Card>
      </div>
      <div className="right-column">
        <Card title="Map Preview">
          <div className="map-preview">
            <XGSLeafletMap
              markers={markers}
              polylines={[polyline]}
              overlay={<div className="xgs-map__informationbox">
                {[{ key: "address", label: 'Address' },
                { key: "service-center", label: 'Service Center' }].map(({ key, label }) => (
                  <div key={key} className={`xgs-map__informationbox__infodata xgs-map__informationbox__infodata--${key}`}>
                    <span></span>
                    {label}
                  </div>
                ))}
              </div>}
            />
          </div>
          <div className="coords">
            <div>Coordinates:</div>
            {address?.latitude && address?.longitude && <div className="coords__value">{Number(address?.latitude)?.toFixed(2)}, {Number(address?.longitude)?.toFixed(2)}</div>}
          </div>
        </Card>

        <Card title="Servicing Terminal" info={<Popover values={<div style={{ fontWeight: "normal" }}>This terminal is assigned based on the ZIP code of the address, according to the route guide.</div>}></Popover>}>
          {isLoadingNearbyDetails ? <div className="loading"><Loading isLoading={true} /></div>
            : <>
              <OutlineRow label="Servicing Terminal" value={nearbyDetails?.servicingTerminal ? (nearbyDetails?.servicingTerminal?.airportCode.toString() + " - "
                + nearbyDetails?.servicingTerminal?.terminalName.toString() + " - "
                + nearbyDetails?.servicingTerminal?.terminalNumber.toString()) : "N/A"} variant="blue" />
              <OutlineRow label="Terminal Code" value={nearbyDetails?.servicingTerminal?.terminalNumber.toString() || "N/A"} />
              <OutlineRow label="Distance to Terminal" value={nearbyDetails?.servicingTerminal ? (nearbyDetails?.servicingTerminal?.distanceMiles.toString() + " miles") : "N/A"} />
              <OutlineRow label="Service Days" value={<ServiceDays openDaysRange={nearbyDetails?.servicingTerminal?.terminalOpenHoursList?.[0]?.openDays || ""} />} />
            </>}
        </Card>
        <Card title={`Associated Accounts ${items.length ? `(${items.length})` : ""}`}>
          <div className="notes-widget__recent__filter">
            <img src={searchIcon} alt="search" className="notes-widget__recent__filter__searchIcon" />
            <LabeledTextInput
              name="searchString"
              labelMode={LabelModes.column}
              className="xgs-sideaddress-list__input"
              type="text"
              value={filter}
              onKeyDown={(e) => (e.key === "Enter")}
              onChange={(e) => setFilter(e.currentTarget.value)}
              placeholder="Search accounts..."
              clearable
              onClear={() => setFilter("")}
            />
          </div>
          <div className="filter-sidebar__content__scrollable_list">
            <div style={{ flex: 1 }}>
              {isLoadingAssociatedAccounts ? (
                <div className="xgs-sideaddress-list__loading"><Loading isLoading={true} /></div>
              ) : items.length === 0 ? (
                <div className="xgs-sideaddress-list__no-results">
                  No accounts found {filter.length > 0 ? `for "${filter}"` : ""}
                </div>
              ) : (
                <InfiniteScrollableList
                  isItemLoaded={isItemLoaded}
                  itemCount={itemCount}
                  loadMoreItems={loadMoreItems}
                  children={Row}
                  itemSize={140}
                />
              )}
            </div>
          </div>
        </Card>

        <Card title="Scheduling & Timing">
          {isLoadingAddressById ? <div className="loading"><Loading isLoading={true} /></div>
            : <>
              <div className="operational-timing__row">
                <div className="operational-timing__label">
                  <Calendar /> Appointments:
                </div>
                <div className="operational-timing__value">
                  {upcomingDetails?.date && upcomingDetails?.earlyTime
                    ? moment(
                      upcomingDetails.date + " " + upcomingDetails.earlyTime,
                      "YYYY-MM-DD HH:mm:ss"
                    ).format("MM/DD/YYYY, h:mm A")
                    : "N/A"}
                </div>
              </div>
            </>}
        </Card>
        <Card title="Driving Instructions" className="scroll">
          {isLoadingAddressById ? <div className="loading"><Loading isLoading={true} /></div>
            : <div className="instructions">
              <div>{address?.drivingInstructions ? address?.drivingInstructions : "N/A"}</div>
            </div>}
        </Card>
      </div>
    </div>
  );
};

export default OverviewWidget;
